import * as React from "react";
import { EnterpriseClient, EnterpriseDemoRequest, EnterpriseDemoRequestMode, IEnterpriseDemoRequest } from "Services/API/API";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { Controller, useForm } from "react-hook-form";
import { useEffect, useState } from "react";
import { InputFL, ValidationSummary, EmailInputFL } from "Components/Inputs";
import { SpinningButton } from "Components/ReactstrapSlim";
import { trackLeadCaptureForm } from "Utils/gaTrackers";
import { useCookies } from "react-cookie";
import ReCAPTCHA from "react-google-recaptcha";
import { useApiClient } from "Services/API/useApiClient";

type SendMessageForm = Pick<IEnterpriseDemoRequest, "email" | "question" | "recaptchaToken"> &
{
   firstName: string,
   lastName: string,
};

const SendMessageFormSchema: yup.SchemaOf<SendMessageForm> = yup.object({
   question: yup.string().required("Message is required").defined(),
   email: yup.string().required("Email is required").email("Email is not valid").defined(),
   firstName: yup.string().required("First name is required").defined(),
   lastName: yup.string().required("Last name is requred").defined(),
   recaptchaToken: yup.string().required("Please confirm you are not a robot").defined()
});

export interface SendMessageFormProps {
   mode: EnterpriseDemoRequestMode
}

export const SendMessageForm: React.FC<SendMessageFormProps> = function ({ mode }) {
   const enterpriseClient = useApiClient(EnterpriseClient);
   const [submitButtonDisabled, setSubmitButtonDisabled] = useState(true);
   const [showSuccessScreen, setShowSuccessScreen] = useState(false);
   const [isSubmitting, setIsSubmitting] = useState(false);
   const [serverError, setServerError] = useState<Array<[string, string]>>([]);
   const [cookies] = useCookies(["hubspotutk"]);

   const formMethods = useForm<SendMessageForm>({
      mode: "onTouched",
      resolver: yupResolver(SendMessageFormSchema)
   })

   const formValues = formMethods.watch();

   const submit = async () => {
      setIsSubmitting(true);
      try {
         const isValid = await formMethods.trigger();
         if (isValid) {
            const data = formMethods.getValues();
            const demoRequest = EnterpriseDemoRequest.fromJS({
               mode: mode,
               phoneNumber: undefined,
               company: undefined,
               plan: undefined,
               solution: undefined,               
               companySize: undefined,
               email: data.email,
               question: data.question,
               fullName: data.firstName + " " + data.lastName,
               hubspotUTK: cookies.hubspotutk,
               referrerQuery: window.location.search,
               recaptchaToken: data.recaptchaToken
            });
            const result = await enterpriseClient.requestDemo(demoRequest);

            if (result.success) {
               setShowSuccessScreen(true);
            } else {
               setServerError([["email", result.errorMessage || "Unexpected server error"]]);
            }

            trackLeadCaptureForm(mode, "submit");
         }
      } catch (e: any) {
         if (e.error) {
            setServerError([["email", e.error]]);
         }
      } finally {
         setIsSubmitting(false);
      }
   }

   useEffect(() => {
      SendMessageFormSchema.isValid(formValues).then(
         isValid => setSubmitButtonDisabled(!isValid)
      );
   }, [formValues.email, formValues.firstName, formValues.lastName, formValues.question, formValues.recaptchaToken]);

   return (
      <div className="send-message-form">
         {!showSuccessScreen &&
            <div className="container">
               <div className="row">
                  <div className="col-12 col-xl-6 about-you-left">
                     <div className="about-you-header">Enough about us.</div>
                     <div className="about-you-header">Let’s talk about you.</div>
                     <div className="about-you-text">Let our experts help answer your questions about deploying a work from anywhere program to meet your company’s needs.</div>
                  </div>
                  <div className="col-12 col-xl-6 about-you-right">
                     <div>
                        <div>
                           <div className="row">
                              <div className="col-12 col-xl-6 left-input-wrapper">
                                 <InputFL control={formMethods.control} name="firstName" placeholder="First Name" onDark />
                              </div>
                              <div className="col-12 col-xl-6 right-input-wrapper">
                                 <InputFL control={formMethods.control} name="lastName" placeholder="Last Name" onDark />
                              </div>
                           </div>
                           <EmailInputFL control={formMethods.control} name="email" placeholder="Email Address" autoComplete="off" onDark />
                           <InputFL control={formMethods.control} name="question" type="textarea" placeholder="Message" autoComplete="off" className="about-message" onDark />
                           <div className="pt-px24">
                              <Controller
                                 control={formMethods.control}
                                 name="recaptchaToken"
                                 render={({ field, fieldState }) =>
                                    <>
                                       <ReCAPTCHA
                                          sitekey={LiquidSpaceConfig.RecaptchaKeys.Explicit.SiteKey!}
                                          onChange={token => field.onChange(token || undefined)}
                                          onExpired={() => field.onChange("")}
                                       />
                                       {
                                          <div className="field-validation-error">{fieldState.error?.message}</div>
                                       }
                                    </>
                                 }/>
                              </div>
                           <ValidationSummary errors={formMethods.formState.errors} serverErrors={serverError} />
                        </div>
                        <div>                           
                           <SpinningButton onClick={submit} isLoading={isSubmitting} disabled={submitButtonDisabled} className="btn-primary send-message-cta" fluid>Get in touch</SpinningButton>
                        </div>
                     </div>
                  </div>
               </div>
            </div>
         }
         {showSuccessScreen &&
            <div className="container">
               <div className="row about-you-row-success">
                  <div className="col-12 col-md-7 about-you-left-success">
                     <div className="about-you-header">Thank you for filling out the form. It has been successfully submitted</div>
                  </div>
                  <div className="col-12 col-md-5 about-you-right-success">
                     <svg className="float-right svg-success-icon" viewBox="0 0 280 280" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <rect width="280" height="280" rx="16" fill="#A1DED2" />
                        <path d="M67.2031 149.416L108.878 190.4L212.803 88.2" stroke="#00293A" strokeWidth="20" strokeLinecap="round" />
                     </svg>
                  </div>
               </div>
            </div>
         }
      </div>
   );
}